import { useEffect, useState } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect, RootStateOrAny } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import moment from 'moment';

import { Typography, Breadcrumb } from 'antd';
import { HomeOutlined, UserOutlined } from '@ant-design/icons';

import notifier from 'utils/notifier';
import { sortByIPA, getDisplayCompanyId } from 'utils/helpers';
import { ErrorResponse, GetAuthSearchProviderListResponse, GetFileTypesResponse, TablePagination } from 'utils/types';

import { getAuthSearchProviderList } from 'store/authorization/provider/actions';
import { getFileTypes } from 'store/parameter/actions';
import { getAuthMember  } from 'store/authorization/member/actions';
import { getProvider } from 'store/eligibility/actions';
import { getHelpCenterLinks } from 'store/app/helpCenter/actions';

import MemberResultsTable from 'components/Auth/CreateAuth/MemberResultTable';
import CreateAuthorizationDetail from 'components/Auth/CreateAuth/CreateAuthorizationDetail';
import CreateAuthorizationConfirmation from 'components/Auth/CreateAuth/CreateAuthorizationConfirmation';
import SelectMemberForm from 'components/Auth/CreateAuth/SelectMemberForm';
import HelpCenterLink from 'components/Common/HelpCenterLink';

import styles from './CreateAuthorization.module.scss';


const { Title } = Typography;

interface CreateAuthorizationPageState {
  getAuthSearchProviderList: Function;
  errorMsg: string;
  getFileTypes: Function;
  getAuthMember: Function;
  isSearching: boolean;
  getProvider(providerKeyId: string, companyId: string): any;
  getHelpCenterLinks(companyId: string, page: string, type: string): any;
};

const CreateAuthorizationPage = (props: CreateAuthorizationPageState) => {
  const [page, setPage] = useState('start');
  const [providerList, setProviderList] = useState([] as any);
  const [tableInfo, setTableInfo] = useState({} as any);
  const [totalMemberResults, setTotalMemberResults] = useState(0);
  const [fileTypes, setFileTypes] = useState([] as any);
  const [authMembers, setAuthMembers] = useState([] as any);

  const [authMember, setAuthMember] = useState({} as any);
  const [provider, setProvider] = useState({} as any);
  const [authNumber, setAuthNumber] = useState('');
  const [retroDos, setRetroDos] = useState('');
  const [accessErrors, setAccessErrors] = useState<string[]>([]);
  const [helpCenter, setHelpCenter] = useState<any>([]);

  const history = useHistory();
  const location: any = useLocation();

  const triggerStartPage = () => setPage('start');

  const triggerCreateAuthPage = (record: any) => {
    const errors = memberAccess(record);
    if (errors.length === 0) {
      setAuthMember(record);
      setPage('create-authorization-page');
    } else {
      setAccessErrors(errors);
    }
  };

  const triggerConfirmAuthPage = (authNumber: any) => {
    setAuthNumber(authNumber);
    setPage('confirmation-page');
    history.push({
     ...location,
      state: {
        ...location.state,
        previousPath: "confirmation-page"
     }
    });
  }

  const getHelpCenterLinks = () => {
    props.getHelpCenterLinks(sessionStorage.getItem('ipaGroup')!, '/auth/create', 'page')
      .then((response: any) => {
        setHelpCenter(response);
    })
    .catch((err: ErrorResponse) => {
      console.log(err?.response?.data?.Message);
    });
  }

  const columns = [
    {
      title: 'IPA',
      key: 'RmiCompanyId',
      dataIndex: 'RmiCompanyId',
      render: (text: string) => getDisplayCompanyId(text)
    },
    {
      title: 'Member ID',
      key: 'RmiMembId',
      dataIndex: 'RmiMembId',
      sorter: (a: any, b: any) => (a.memberRefId > b.memberRefId ? -1 : 1),
      render: (text: string, record: any) => <a onClick={() => record.RmiHpCode === 'BSPP' ? notifier.error('Patient is a Blue Shield PPO member - please follow Blue Shield procedure for the referrals', '', 5)  : triggerCreateAuthPage(record)}>{text}</a>
    },
    {
      title: 'Last Name',
      key: 'RmiLastNm',
      dataIndex: 'RmiLastNm',
      sorter: (a: any, b: any) => (a.lastName > b.lastName ? -1 : 1),
    },
    {
      title: 'First Name',
      key: 'RmiFirstNm',
      dataIndex: 'RmiFirstNm',
      sorter: (a: any, b: any) => (a.firstName > b.firstName ? -1 : 1),
    },
    {
      title: 'Gender',
      key: 'RmiSex',
      dataIndex: 'RmiSex',
    },
    {
      title: 'Date of Birth',
      key: 'RmiBirth',
      dataIndex: 'RmiBirth',
      sorter: (a: any, b: any) => a.RmiBirth.localeCompare(b.RmiBirth),
      render: (text: string) => <>{text ? moment(text).format('MM/DD/YYYY') : ''}</>
    },
    {
      title: 'Language',
      key: 'RmiLanguage',
      dataIndex: 'RmiLanguage',
      sorter: (a: any, b: any) => a.RmiLanguage.localeCompare(b.RmiLanguage),
    },
    {
      title: 'Health Plan',
      key: 'RmiHpName',
      dataIndex: 'RmiHpName',
    },
    {
      title: 'Option',
      key: 'RmiOpt',
      dataIndex: 'RmiOpt',
    },
    {
      title: 'Start Date',
      key: 'RmiStartDate',
      dataIndex: 'RmiStartDate',
      render: (text: string) => <>{text ? moment(text).format('MM/DD/YYYY') : ''}</>
    },
    {
      title: 'Term Date',
      key: 'RmiEndDate',
      dataIndex: 'RmiEndDate',
      render: (text: string) => <>{text ? moment(text).format('MM/DD/YYYY') : ''}</>
    },
    {
      title: 'Capitated Hospital',
      key: 'Capitated',
      dataIndex: 'Capitated',
      render: (isCapitated: boolean) => <>{isCapitated ? 'YES' : 'NO'}</>
    }
  ];

  const updateTablePagination = (data: TablePagination) => {
    setTableInfo(data);
  }

  const updateTableData = (data: any) => {
    setTotalMemberResults(data?.length);
    setAuthMembers(data);
    sessionStorage.setItem('auth_create_search_result', JSON.stringify(data));
  };

  //@TODO - this is a temporary solution to keep the previous provider selected when going back with select member on the breadcrumb
  useEffect(() => {
    if (page === 'start') {
      setProvider(provider);
    }
  }, [page, provider]);

  useEffect(() => {
    props.getAuthSearchProviderList({UserId: sessionStorage.user_id, UserRole: sessionStorage.user_role})
    .then((response: GetAuthSearchProviderListResponse[]) => {
      response?.length !== 0 ? setProviderList(sortByIPA(response, 'Provider')) : notifier.error('Oh no!', 'None of the providers associated with this User has write access to the auth module. No authorization can be created.');
    })
    .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data.Message || 'Something went wrong, please contact a system administrator.');
    });

    props.getFileTypes("ImageExtension")
    .then((response: GetFileTypesResponse) => {
        setFileTypes(response);
    })
    .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data.Message || 'Something went wrong, please contact a system administrator.');
    });

    savedSearchResults();
    getHelpCenterLinks();
  }, []);


  // This is to save the search result that will be used for the auth confirmation page
  const savedSearchResults = () => {
    if (location?.state?.previousPath === '/auth/create') {
      setAuthMembers(JSON.parse(sessionStorage.getItem('auth_create_search_result')!));
    } else {
      setAuthMembers([]);
      sessionStorage.removeItem('auth_create_search_result');
    }
  }

  //TODO - Ported the validation logics over from legacy but temporary commenting out some of the validation to limit scope to hotfix for CRCAMC - workitem 30459
  const memberAccess = (member: any) => {
    let msgList: string[] = [];
    // let startDate = null;
    // let endDate = null;
    // let today = null;

    //convert member's end/start date to correct timezone
    // if (member.RmiEndDate) {
    //   var tDate = member.RmiEndDate.split("T");
    //   endDate = new Date(tDate[0]).setHours(0, 0, 0, 0);
    //   //new Date obj is decreasing by 1 day, so here we add it back
    //   endDate = new Date(endDate + 24 * 60 * 60 * 1000);
    // }

    // if (member.RmiStartDate) {
    //     var sDate = member.RmiStartDate.split("T");
    //   startDate = new Date(sDate[0]).setHours(0, 0, 0, 0);
    //   startDate = new Date(startDate + 24 * 60 * 60 * 1000);
    // }

    // if (retroDos == null) {
    //   today = new Date(moment().toISOString()).setHours(0, 0, 0, 0);
    //   today = new Date(today);
    // }

    //TODO - test later
    // if (member.RmiEndDate !== null && retroDos == null) {
    //   msgList.push("Only retro authorizations may be submitted for termed members. \n Please enter a retro DOS and try again.");
    //   return;
    // } else if (member.RmiEndDate !== null && retroDos !== null && endDate && new Date(retroDos) > endDate) {
    //     msgList.push("Date of service must be on or before member's termed date.");
    //     return;
    // } else if (member.RmiStartDate !== null && retroDos !== null && startDate && startDate > new Date(retroDos)) {
    //     msgList.push("Date of service must be on or after member's start date.");
    //     return;
    // } else if (retroDos == null && member.RmiStartDate !== null && startDate && today && startDate > today) {
    //     msgList.push("This member's plan is not active as of today. Please contact Network Medical Management for assistance.");
    //     return;
    // } else if (member.RmiCompanyId !== search.CompanyId) {
    //     msgList.push("Please select a provider with the same IPA (" + member.RmiCompanyId + ")as the member.");
    //     return;
    // }

    //TODO - test later
    // if (member.RmiHpCode === "BSPP") {
    //   msgList.push("Patient is a Blue Shield PPO member - please follow Blue Shield procedure for the referrals");
    //   return msgList;
    // }

    //TODO - test later
    // else if (member.RmiCompanyId !== search.CompanyId) {
    //   msgList.push(`Please select a provider with the same IPA (${member.RmiCompanyId})as the member.`);
    //   return;
    // }

    //TODO - test later
    // else if (member.RmiCompanyId === Company.AHC && member.RmiHpCode === "CHPS" && retroDos !== null && moment(retroDos) < moment("1/1/2018")) {
    //   msgList.push("For Central Health Plan Members: Accountable Health Care IPA is not delegated for Utilization Management for the period before 01/01/2018. Please submit your referral requests to Central Health MSO. If you have any questions, you may contact them at 626-388-2300.");
    //   return msgList;
    // }

    return msgList;
  }

  return (
    <div className={styles['create-authorization-page']}>
      <div className={styles['header-title']}>
        <Title level={4}>&nbsp;&nbsp;&nbsp;Create Authorization</Title>
      </div>

      {page === 'start' && (
        <>
        <div>
        <Breadcrumb>
          <Breadcrumb.Item>
            <HomeOutlined
              onClick={() => history.push('/quality/overview')}
            />
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <UserOutlined />
            <a>Select Member</a>
          </Breadcrumb.Item>
        </Breadcrumb>
        {
          helpCenter?.length > 0 ? helpCenter.map((hc: any) => {
            return (
              <>
                <div className={`row ${styles['help-center-link']}`}>
                  <div className="col-md-24">
                    <HelpCenterLink
                      clickEventId={hc.ClickEventId}
                      linkName={hc.LinkName}
                      pagePath={hc.PagePath}
                      url={hc.Link}
                      companyId={hc.CompanyId}
                    />
                  </div>
                </div>
              </>
            );
          }) : null
        }
      </div>
        <div>
          <div className="search-section">
            <SelectMemberForm
              tablePagination={tableInfo}
              updateTableData={updateTableData}
              providerList={providerList}
              fileTypes={fileTypes}
              getAuthMember={props.getAuthMember}
              provider={provider}
              setProvider={setProvider}
              retroDos={retroDos}
              setRetroDos={setRetroDos}
              accessErrors={accessErrors}
              setAccessErrors={setAccessErrors}
            />
          </div>
          {authMembers && authMembers.length !== 0 && (
            <div className="content">
              <Title level={4}>Members</Title>
                <MemberResultsTable
                  updateTablePagination={updateTablePagination}
                  dataSource={authMembers}
                  columns={columns}
                  totalMemberResults={totalMemberResults}
                  loading={props.isSearching}
                />
            </div>
          )}
        </div>
        </>
      )}
      {page === 'create-authorization-page' && (
        <>
        <div>
          <Breadcrumb>
            <Breadcrumb.Item>
              <HomeOutlined
                onClick={() => history.push('/quality/overview')}
              />
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <UserOutlined />
              <a onClick={triggerStartPage}>Select Member</a>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <UserOutlined />
              <a>Create Authorization</a>
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <CreateAuthorizationDetail
          authMember={authMember}
          provider={provider}
          retroDos={retroDos}
          setRetroDos={setRetroDos}
          triggerStartPage={triggerStartPage}
          triggerConfirmAuthPage={triggerConfirmAuthPage}
        />
        </>
      )}
      {page === 'confirmation-page' && (
        <div>
          <div>
            <Breadcrumb>
              <Breadcrumb.Item>
                <HomeOutlined
                  onClick={() => history.push('/quality/overview')}
                />
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <UserOutlined />
                <a onClick={triggerStartPage}>Select Member</a>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <UserOutlined />
                <a onClick={() => setPage('create-authorization-page')}>Create Authorization</a>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <UserOutlined />
                <a>Authorization Confirmation</a>
              </Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <CreateAuthorizationConfirmation
            authNumber={authNumber}
            setPage={setPage}
          />
        </div>
      )}
    </div>
  );

};

const mapStateToProps = (store: RootStateOrAny) => ({
  errorMsg: store.rootReducer.authProvider.errorMsg,
  isSearching: store.rootReducer.authMember.isFetching,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
  {
    getAuthSearchProviderList,
    getFileTypes,
    getAuthMember,
    getProvider,
    getHelpCenterLinks,
  },
  dispatch,
);
export default connect(mapStateToProps, mapDispatchToProps)(CreateAuthorizationPage);